import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, Download, Upload, Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = {
  kokan:   { btn: 'bg-blue-600 hover:bg-blue-700',   hdr: 'bg-blue-900' },
  rh:      { btn: 'bg-emerald-600 hover:bg-emerald-700', hdr: 'bg-emerald-900' },
  mkt:     { btn: 'bg-amber-600 hover:bg-amber-700',  hdr: 'bg-amber-900' },
  overview:{ btn: 'bg-slate-800 hover:bg-slate-900',  hdr: 'bg-slate-900' },
};

const fmtINR = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  if (n >= 10_000_000) return `₹${(n/10_000_000).toFixed(2)} Cr`;
  if (n >= 100_000)    return `₹${(n/100_000).toFixed(2)} L`;
  if (n >= 1_000)      return `₹${(n/1_000).toFixed(1)} K`;
  return `₹${n.toFixed(0)}`;
};

// Try parsing common date formats: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, DD-MMM-YY, DD-MMM-YYYY
const MONTHS = { JAN:0,FEB:1,MAR:2,APR:3,MAY:4,JUN:5,JUL:6,AUG:7,SEP:8,OCT:9,NOV:10,DEC:11 };
const parseExpiryDate = (val) => {
  if (val === null || val === undefined) return null;
  const s = String(val).trim();
  if (s.length < 6) return null;
  let m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) {
    const d = parseInt(m[1], 10), mo = parseInt(m[2], 10) - 1;
    let y = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
    if (mo >= 0 && mo <= 11 && d >= 1 && d <= 31) {
      const date = new Date(y, mo, d);
      if (!isNaN(date.getTime())) return date;
    }
  }
  m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) {
    const date = new Date(+m[1], +m[2] - 1, +m[3]);
    if (!isNaN(date.getTime())) return date;
  }
  m = s.match(/^(\d{1,2})[-\s/]([A-Za-z]{3})[-\s/](\d{2,4})$/);
  if (m) {
    const mo = MONTHS[m[2].toUpperCase()];
    if (mo !== undefined) {
      let y = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
      const date = new Date(y, mo, +m[1]);
      if (!isNaN(date.getTime())) return date;
    }
  }
  return null;
};

// Returns the alert tier for a date column value: 'overdue' | 'urgent' | 'soon' | null
const expiryTier = (val) => {
  const d = parseExpiryDate(val);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((d.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return 'overdue';
  if (diffDays < 7) return 'urgent';
  if (diffDays < 30) return 'soon';
  return null;
};

const EXPIRY_KEYWORDS = ['DATE', 'INSURANCE', 'PUC', 'FITNESS', 'SERVICING', 'EXPIRY', 'RENEW'];
const isExpiryColumn = (colName) => {
  if (!colName) return false;
  const upper = String(colName).toUpperCase();
  return EXPIRY_KEYWORDS.some(k => upper.includes(k));
};

const EXPIRY_CLASS = {
  overdue: 'bg-red-200 text-red-900 font-semibold',
  urgent:  'bg-red-100 text-red-800 font-semibold',
  soon:    'bg-amber-100 text-amber-900 font-semibold',
};

// Status-based cell coloring (used by Site Ops Project Stages matrix)
const statusClass = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val).trim().toUpperCase();
  if (!s) return '';
  if (s === 'DONE' || s === 'COMPLETED' || s === 'OK')              return 'bg-emerald-100 text-emerald-900 font-semibold';
  if (s === 'INPROCESS' || s === 'IN PROCESS' || s === 'IN WORKING' || s === 'WIP') return 'bg-amber-100 text-amber-900 font-semibold';
  if (s === 'HOLD' || s === 'PENDING' || s === 'BLOCKED')           return 'bg-red-100 text-red-900 font-semibold';
  if (s === 'NILL' || s === 'NIL' || s === 'NA' || s === 'N/A')     return 'bg-slate-100 text-slate-500 italic';
  return '';
};

const FinSheet = ({ datasetKey, accent, numericCols = [], apiPrefix = '/finance', enableExpiryAlerts = false, enableStatusColors = false, columnFilter = null, rowFilter = null, defaultRowValues = {} }) => {
  const ac = ACCENT[accent] || ACCENT.kokan;
  const prefix = apiPrefix;
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [newColName, setNewColName] = useState('');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  const inputRef = useRef(null);
  const importRef = useRef(null);
  const tableWrapRef = useRef(null);
  const tableElRef = useRef(null);
  const topBarRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [colRes, rowRes] = await Promise.all([
        fetch(`${API}${prefix}/datasets/${datasetKey}/columns`),
        fetch(`${API}${prefix}/datasets/${datasetKey}/rows`),
      ]);
      setColumns((await colRes.json()).columns || []);
      setRows((await rowRes.json()).rows || []);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [datasetKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (editCell && inputRef.current) inputRef.current.focus(); }, [editCell]);

  // Keep the top scrollbar width in sync with the table's actual width (measure after paint)
  useEffect(() => {
    const measure = () => setScrollW(tableElRef.current?.offsetWidth || tableWrapRef.current?.scrollWidth || 0);
    const raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    const t1 = setTimeout(measure, 300);
    const t2 = setTimeout(measure, 900);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener('resize', measure);
    };
  }, [columns, rows, loading]);

  const addRow = async () => {
    try {
      const res = await fetch(`${API}${prefix}/datasets/${datasetKey}/rows`, { method: 'POST' });
      const newRow = await res.json();
      // Pre-fill default values (e.g. today's date) if any defaults are configured
      const defaultEntries = Object.entries(defaultRowValues);
      if (defaultEntries.length > 0 && columns.length > 0) {
        const colByName = Object.fromEntries(columns.map(c => [c.name.toUpperCase(), c.id]));
        const data = {};
        defaultEntries.forEach(([colName, val]) => {
          const colId = colByName[colName.toUpperCase()];
          if (colId) data[colId] = val;
        });
        if (Object.keys(data).length > 0) {
          const patchRes = await fetch(`${API}${prefix}/datasets/${datasetKey}/rows/${newRow.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data }),
          });
          const updated = await patchRes.json();
          setRows(prev => [...prev, updated]);
          return;
        }
      }
      setRows(prev => [...prev, newRow]);
    } catch { toast.error('Failed to add row'); }
  };

  const deleteRow = async (rowId) => {
    try {
      await fetch(`${API}${prefix}/datasets/${datasetKey}/rows/${rowId}`, { method: 'DELETE' });
      setRows(prev => prev.filter(r => r.id !== rowId));
    } catch { toast.error('Failed to delete row'); }
  };

  const addColumn = async () => {
    if (!newColName.trim()) return;
    try {
      const res = await fetch(`${API}${prefix}/datasets/${datasetKey}/columns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newColName.trim() }),
      });
      const newCol = await res.json();
      setColumns(prev => [...prev, newCol]);
      setNewColName('');
    } catch { toast.error('Failed to add column'); }
  };

  const saveEdit = async () => {
    if (!editCell) return;
    const { rowId, colId } = editCell;
    try {
      const res = await fetch(`${API}${prefix}/datasets/${datasetKey}/rows/${rowId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { [colId]: editVal } }),
      });
      const updated = await res.json();
      setRows(prev => prev.map(r => r.id === rowId ? updated : r));
    } catch { toast.error('Failed to save'); }
    setEditCell(null);
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await (await fetch(`${API}${prefix}/datasets/${datasetKey}/export`)).blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${datasetKey}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Exported successfully');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}${prefix}/datasets/${datasetKey}/import`, { method: 'POST', body: fd });
      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = null; }
      if (!res.ok) {
        const msg = (data && data.detail) ? data.detail
          : `Import failed (server error ${res.status}). Please check the file and try again.`;
        throw new Error(msg);
      }
      if (!data) {
        throw new Error('Import did not complete cleanly. Please retry — if it repeats, the file may be too large or still open in Excel.');
      }
      toast.success(`Imported ${data.rows_imported} rows${data.columns_created > 0 ? ` + ${data.columns_created} new cols` : ''} (replaced previous data)`);
      await load();
    } catch (err) { toast.error(err.message || 'Import failed'); }
    setImporting(false);
    if (importRef.current) importRef.current.value = '';
  };

  const totals = {};
  const visibleColumns = columnFilter ? columns.filter(c => columnFilter(c.name)) : columns;
  const colByName = Object.fromEntries(columns.map(c => [c.name, c.id]));
  const baseRows = rowFilter ? rows.filter(r => rowFilter(r, colByName)) : rows;

  // Search across all visible column values
  const searchedRows = searchTerm.trim()
    ? baseRows.filter(r =>
        visibleColumns.some(col => {
          const v = String(r.data?.[col.id] ?? '').toLowerCase();
          return v.includes(searchTerm.toLowerCase());
        })
      )
    : baseRows;

  // Pagination
  const totalPages = Math.max(1, Math.ceil(searchedRows.length / PAGE_SIZE));
  const safePage   = Math.min(currentPage, totalPages);
  const visibleRows = searchedRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  visibleColumns.forEach(col => {
    if (numericCols.includes(col.name)) {
      totals[col.id] = searchedRows.reduce((s, r) => s + (parseFloat(r.data?.[col.id]) || 0), 0);
    }
  });

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <Input value={newColName} onChange={e => setNewColName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addColumn()}
            placeholder="New column name..." className="h-8 w-44 text-sm" />
          <Button size="sm" variant="outline" className="h-8" onClick={addColumn}><Plus className="w-3.5 h-3.5 mr-1" />Col</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={addRow} data-testid={`add-row-${datasetKey}`}><Plus className="w-3.5 h-3.5 mr-1" />Row</Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="h-8" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
          <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} />
          <Button size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => importRef.current?.click()} disabled={importing} data-testid={`import-btn-${datasetKey}`}>
            <Upload className="w-3.5 h-3.5 mr-1" />{importing ? 'Importing…' : 'Import Excel'}
          </Button>
          <Button size="sm" className={`h-8 text-white ${ac.btn}`} onClick={exportExcel} disabled={exporting}>
            <Download className="w-3.5 h-3.5 mr-1" />{exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search across all columns…"
            className="h-8 pl-8 text-sm"
            data-testid={`search-${datasetKey}`}
          />
          {searchTerm && (
            <button onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              ×
            </button>
          )}
        </div>
        <span className="text-xs text-slate-400 whitespace-nowrap">
          {searchedRows.length} of {rows.length} row(s){searchTerm && ' matched'}
        </span>
      </div>

      {/* Top horizontal scrollbar (synced with the table below) */}
      <div
        ref={topBarRef}
        className="overflow-x-auto rounded-t-lg"
        style={{ height: 16 }}
        onScroll={() => { if (tableWrapRef.current && topBarRef.current) tableWrapRef.current.scrollLeft = topBarRef.current.scrollLeft; }}
        data-testid={`top-scroll-${datasetKey}`}
      >
        <div style={{ width: scrollW || 1, height: 1 }} />
      </div>

      <div
        ref={tableWrapRef}
        className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm"
        onScroll={() => { if (topBarRef.current && tableWrapRef.current) topBarRef.current.scrollLeft = tableWrapRef.current.scrollLeft; }}
      >
        <table ref={tableElRef} className="w-full text-xs border-collapse min-w-[600px]">
          <thead>
            <tr className={`${ac.hdr} text-white`}>
              <th className="px-3 py-2.5 text-center font-semibold border border-slate-600 w-8">#</th>
              {visibleColumns.map(col => (
                <th key={col.id} className="px-3 py-2.5 text-left font-semibold border border-slate-600 whitespace-nowrap min-w-[90px]">{col.name}</th>
              ))}
              <th className="px-2 py-2.5 text-center border border-slate-600 w-8">Del</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={visibleColumns.length + 2} className="text-center py-8 text-slate-400 italic">No data yet. Click &quot;+ Row&quot; to add entries.</td></tr>
            )}
            {visibleRows.map((row, ri) => (
              <tr key={row.id} className={ri % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50 hover:bg-slate-100'}>
                <td className="px-3 py-2 text-center text-slate-400 border border-slate-200 font-mono text-[10px]">{ri + 1}</td>
                {visibleColumns.map(col => {
                  const val = row.data?.[col.id] ?? '';
                  const isEditing = editCell?.rowId === row.id && editCell?.colId === col.id;
                  const isNum = numericCols.includes(col.name);
                  const tier = enableExpiryAlerts && !isEditing && isExpiryColumn(col.name) ? expiryTier(val) : null;
                  const expiryCls = tier ? EXPIRY_CLASS[tier] : '';
                  const statusCls = (enableStatusColors && !isEditing && !expiryCls) ? statusClass(val) : '';
                  const alertCls = expiryCls || statusCls;
                  return (
                    <td key={col.id} className={`px-2 py-1.5 border border-slate-200 cursor-pointer ${isNum ? 'text-right font-mono' : ''} ${alertCls}`}
                      title={tier === 'overdue' ? 'OVERDUE — needs immediate renewal' : tier === 'urgent' ? 'Expires within 7 days' : tier === 'soon' ? 'Expires within 30 days' : undefined}
                      data-testid={tier ? `expiry-${tier}-${col.id}-${row.id}` : undefined}
                      onClick={() => { if (!isEditing) { setEditCell({ rowId: row.id, colId: col.id }); setEditVal(val); } }}>
                      {isEditing ? (
                        <input ref={inputRef} value={editVal} onChange={e => setEditVal(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditCell(null); }}
                          className="w-full border border-blue-400 rounded px-1 py-0.5 text-xs outline-none bg-blue-50" />
                      ) : (
                        <span className={val ? 'text-slate-800' : 'text-slate-300 italic'}>{val !== '' ? val : '—'}</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-center border border-slate-200">
                  <button onClick={() => deleteRow(row.id)} className="text-red-400 hover:text-red-600 p-0.5"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
          {Object.keys(totals).length > 0 && visibleRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-100 font-bold text-xs border-t-2 border-slate-300">
                <td className="px-3 py-2 text-center border border-slate-300 text-slate-600">TOT</td>
                {visibleColumns.map(col => (
                  <td key={col.id} className={`px-2 py-2 border border-slate-300 ${numericCols.includes(col.name) ? 'text-right text-slate-800' : ''}`}>
                    {totals[col.id] !== undefined ? fmtINR(totals[col.id]) : ''}
                  </td>
                ))}
                <td className="border border-slate-300" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-slate-400 text-right">{visibleRows.length} row(s){rowFilter && visibleRows.length !== rows.length ? ` (filtered from ${rows.length})` : ''} — click any cell to edit</p>
      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between flex-wrap gap-2" data-testid={`pagination-${datasetKey}`}>
          <span className="text-xs text-slate-500">
            Page {safePage} of {totalPages} · showing {((safePage-1)*PAGE_SIZE)+1}–{Math.min(safePage*PAGE_SIZE, searchedRows.length)} of {searchedRows.length}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setCurrentPage(1)} disabled={safePage <= 1}>
              «
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={safePage <= 1} data-testid={`prev-page-${datasetKey}`}>
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={safePage >= totalPages} data-testid={`next-page-${datasetKey}`}>
              Next <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setCurrentPage(totalPages)} disabled={safePage >= totalPages}>
              »
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export { FinSheet, fmtINR };
export default FinSheet;
